// Listar y resetear sesiones activas de un bot. Util cuando el operador
// despliega cambios de flow y necesita forzar a las sesiones EN CURSO a
// re-arrancar desde el entry flow.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api-client';

export interface ActiveSession {
  id: number;
  userPhone: string;
  flowId: string | null;
  stepIndex: number | null;
  status: string;
  expiresAt: string;
  updatedAt: string;
  createdAt: string;
  userName: string | null;
}

export interface ActiveSessionsResponse {
  sessions: ActiveSession[];
  total: number;
  partial: boolean;
}

export function activeSessionsQueryKey(botId: number): readonly unknown[] {
  return ['bots', botId, 'sessions', 'active'] as const;
}

export function useActiveSessions(botId: number, enabled = true) {
  return useQuery({
    queryKey: activeSessionsQueryKey(botId),
    queryFn: () => api.get<ActiveSessionsResponse>(`/api/v1/bots/${botId}/sessions/active`),
    enabled,
    // Refresca cada 10s para que el panel refleje sesiones que llegaron o
    // expiraron mientras el operador miraba.
    refetchInterval: 10_000,
  });
}

export function useResetSession(botId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: number) =>
      api.delete<void>(`/api/v1/bots/${botId}/sessions/${sessionId}`),
    onSuccess: () => {
      toast.success(
        'Sesion expirada. El siguiente mensaje del usuario arrancara desde el entry flow.',
      );
      void qc.invalidateQueries({ queryKey: activeSessionsQueryKey(botId) });
    },
    onError: (err) => {
      toast.error(`No se pudo expirar la sesion: ${(err as Error).message}`);
    },
  });
}
